import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AngularSvgIconModule, SvgIconComponent } from 'angular-svg-icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { AuthService } from '../../core/services/auth.service';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LayoutService } from '../../core/services/layout.service';
import {
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../features/organizations/organization.interface';
import { RolePermissionService } from '../../core/services/role-permission.service';
import { SupportLoginService } from '../../features/profile/support-login/support-login.service';
import { AuthResult } from '../../core/models/auth.models';

import { EnvironmentService } from '../../core/services/environment.service';
import { AppService } from '../../core/services/app.service';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationFilterType } from '../../core/models/notification.model';
import { BroadcastService } from '../../features/broadcast-config/broadcast.service';
import { LabelKey } from '../../core/models/report-labels.model';
import { ReportLabelsService } from '../../features/manage-settings/report-labels/report-labels.service';
import { CommonService } from '../../shared/services/common.service';
import { OrganizationSelectionService } from '../../core/services/organization-selection.service';

export interface menu {
  title: string;
  permission: string;
  route: string;
  svg: string;
  children: menu[];
}
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    AngularSvgIconModule,
    NzLayoutModule,
    NzMenuModule,
    RouterModule,
    NzModalModule,
    NzButtonModule,
    SvgIconComponent,
    CommonModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly optionsMenuPermissions = [
    'manage_lms_settings',
    'manage_frequency_settings',
    'manage_sftp',
    'manage_options',
  ];
  private readonly sanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly environmentService = inject(EnvironmentService);
  private readonly appService = inject(AppService);
  loading = false;
  orgData: OrganizationDetailModel | null = null;
  iframeSrc: SafeResourceUrl =
    this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
  userData: userDetailModel | null = null;
  fullName = '';
  supportUserData: AuthResult | null = null;
  private readonly destroy$ = new Subject<void>();
  private resizeSubscription: Subscription = new Subscription();
  isSupport = false;
  isSwitchSchool = false;
  isSupportLogin = false;
  isCollapsed = true;
  isMobile = false;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;
  filterMenu: menu[] = [];
  orgList: { id: number; parentOrganizationId?: number | null }[] = [];
  isParentOrganization = true;
  menu: menu[] = [
    {
      title: 'Home',
      permission: 'view_home',
      route: '/dashboard',
      svg: '/assets/icon/home-ico.svg',
      children: [],
    },
    {
      title: 'Activity Dashboard',
      permission: 'view_activity_dashboard',
      route: '/activity-dashboard',
      svg: '/assets/icon/activity-dashboard-ico.svg',
      children: [],
    },
    {
      title: 'Dashboard',
      permission: 'view_dashboard',
      route: '/dashboard-activity',
      svg: '/assets/icon/activity-dashboard-ico.svg',
      children: [],
    },
    {
      title: 'Insights',
      permission: 'view_insights',
      route: '/insights',
      svg: '/assets/icon/icon-insights.svg',
      children: [],
    },
    {
      title: 'Administration',
      permission: 'view_home',
      route: '',
      svg: '/assets/icon/administration-ico.svg',
      children: [
        {
          title: 'User Management',
          permission: 'admin_usermanagement',
          route: '/user-management',
          svg: '',
          children: [],
        },
        {
          title: 'Organizations',
          permission: 'admin_organizations',
          route: '/organizations',
          svg: '',
          children: [],
        },
        {
          title: 'User Search',
          permission: 'admin_user_search',
          route: '/user-search',
          svg: '',
          children: [],
        },
        {
          title: 'Content',
          permission: 'admin_content',
          route: '/content',
          svg: '',
          children: [],
        },
        {
          title: 'Strategies',
          permission: 'admin_strategies',
          route: '/strategies',
          svg: '',
          children: [],
        },
        {
          title: 'Learners',
          permission: 'admin_learners',
          route: '/learners',
          svg: '',
          children: [],
        },
        {
          title: 'Insights Admin',
          permission: 'admin_insight_admin',
          route: '/insight-admin',
          svg: '',
          children: [],
        },
        {
          title: this.fidelityCheckLabel,
          permission: 'admin_fidelity_check',
          route: '/fidelity-check',
          svg: '',
          children: [],
        },
        {
          title: 'Broadcast Config',
          permission: 'admin_broadcast_config',
          route: '/broadcast-config',
          svg: '',
          children: [],
        },
      ],
    },
    {
      title: 'Manage Settings',
      permission: 'view_manage_settings',
      route: '/manage-settings',
      svg: '/assets/icon/manage-setting-ico.svg',
      children: [
        {
          title: 'BIP Template',
          permission: 'admin_bip_template',
          route: '/bip-template',
          svg: '',
          children: [],
        },
        {
          title: 'Custom Time',
          permission: 'manage_custom_time',
          route: '/manage-settings/custom-time',
          svg: '',
          children: [],
        },
        {
          title: 'Custom Question',
          permission: 'manage_custom_questions',
          route: '/manage-settings/custom-question',
          svg: '',
          children: [],
        },
        {
          title: 'Questionnaire',
          permission: 'manage_questionaire',
          route: '/manage-settings/questionnaire',
          svg: '',
          children: [],
        },
        {
          title: 'Logo',
          permission: 'manage_logo',
          route: '/manage-settings/logo',
          svg: '',
          children: [],
        },
        {
          title: 'Options',
          permission: '',
          route: '/manage-settings/options',
          svg: '',
          children: [],
        },
        {
          title: this.fidelityCheckLabel,
          permission: 'manage_fidelity_check',
          route: '/manage-settings/fidelity-check',
          svg: '',
          children: [],
        },
        {
          title: 'Reference Materials',
          permission: 'manage_reference_materials',
          route: '/manage-settings/reference-materials',
          svg: '',
          children: [],
        },
        {
          title: 'Titles',
          permission: 'manage_titles',
          route: '/manage-settings/titles',
          svg: '',
          children: [],
        },
        {
          title: 'Behavior Goals',
          permission: 'manage_behavior_goals',
          route: '/manage-settings/behavior-goals',
          svg: '',
          children: [],
        },
        {
          title: 'Notes',
          permission: 'manage_notes',
          route: '/manage-settings/notes',
          svg: '',
          children: [],
        },
        {
          title: 'Report Labels',
          permission: 'manage_report_labels',
          route: '/manage-settings/report-labels',
          svg: '',
          children: [],
        },
      ],
    },
    {
      title: 'Reports',
      permission: 'view_reports',
      route: '/reports',
      svg: '/assets/icon/report-ico.svg',
      children: [],
    },
    {
      title: 'Identity Provider',
      permission: 'view_saml_config',
      route: '/idp',
      svg: '/assets/icon/saml-ico.svg',
      children: [],
    },
    {
      title: 'E-Learning',
      permission: 'view_elearning',
      route: '/lms',
      svg: '/assets/icon/icon-insights.svg',
      children: [],
    },
    {
      title: 'Activity Logs',
      permission: 'view_audit_logs',
      route: '/audit-logs',
      svg: '/assets/icon/report-ico.svg',
      children: [],
    },
  ];
  selectedOrgId: number | null = 0;

  async onLogout(): Promise<void> {
    this.loading = true;
    this.hitBackgroundUrl();

    this.layoutService.setSidebarState(true);
    document.body.style.overflow = '';

    this.notificationService.clearNotifications();

    const timeoutPromise = new Promise<void>(resolve => {
      setTimeout(() => {
        console.warn(
          'SignalR disconnect timed out, proceeding with logout anyway'
        );
        resolve();
      }, 2000);
    });

    try {
      await Promise.race([this.appService.disconnectSignalR(), timeoutPromise]);
      this.authService.signOut();
    } catch (error) {
      console.error('Error during logout process:', error);
      this.loading = false;
      this.supportLoginService.supportUserData.next(null);
      this.router.navigate(['/login']);
    }
  }

  unreadNotificationCount = 0;
  isNewNotification = false;
  private lastNotificationCount = 0;

  constructor(
    private readonly layoutService: LayoutService,
    private readonly cd: ChangeDetectorRef,
    private readonly rbac: RolePermissionService,
    private readonly supportLoginService: SupportLoginService,
    private readonly notificationService: NotificationService,
    private readonly broadcastService: BroadcastService,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly commonService: CommonService,
    private readonly orgSelectionService: OrganizationSelectionService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.fidelityCheckLabel = value;
        const adminMenu = this.menu.find(
          item => item.title === 'Administration'
        );
        if (adminMenu?.children) {
          const fidelityCheckItem = adminMenu.children.find(
            item => item.permission === 'admin_fidelity_check'
          );
          if (fidelityCheckItem) {
            fidelityCheckItem.title = value;
            this.filterMenu = this.getVisibleMenu(this.menu);
          }
        }
        const manageSettingsMenu = this.menu.find(
          item => item.title === 'Manage Settings'
        );
        const mssFidelityItem = manageSettingsMenu?.children?.find(
          item => item.permission === 'manage_fidelity_check'
        );
        if (mssFidelityItem) {
          mssFidelityItem.title = value;
          this.filterMenu = this.getVisibleMenu(this.menu);
        }
      });
    this.orgSelectionService.parentOrgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(parentOrgId => {
        this.isParentOrganization = parentOrgId !== null;
        this.filterMenu = this.getVisibleMenu(this.menu);
      });
    this.authService.userData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.userData = res;
      if (this.userData)
        this.userData.fullName = `${this.userData?.firstName} ${this.userData?.lastName}`;
    });
    this.supportLoginService.supportUserData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.supportUserData = res;
      });
    this.checkPermission();
    this.filterMenu = this.getVisibleMenu(this.menu);
    this.rbac.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.filterMenu = this.getVisibleMenu(this.menu);
      setTimeout(() => this.cd.detectChanges(), 0);
      this.checkPermission();
    });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        if (count > this.lastNotificationCount) {
          this.isNewNotification = true;
          setTimeout(() => {
            this.isNewNotification = false;
          }, 3000);
        }

        this.unreadNotificationCount = count;
        this.lastNotificationCount = count;
        this.cd.markForCheck();
      });
  }

  checkPermission() {
    this.checkSupportPermission();
    this.checkSupportLoginPermission();
  }
  getVisibleMenu(menu: menu[]): menu[] {
    return menu
      .map(item => {
        if (item.children && item.children.length > 0) {
          if (item.route === '/manage-settings') {
            const visibleChildren = item.children.filter(child => {
              if (!this.isParentOrganization) {
                const allowedRoutes = new Set([
                  '/bip-template',
                  '/manage-settings/custom-time',
                  '/manage-settings/options',
                ]);

                if (!allowedRoutes.has(child.route)) {
                  return false;
                }
              }

              return this.hasMenuPermission(child);
            });

            if (visibleChildren.length > 0) {
              return { ...item, children: visibleChildren };
            }

            return null;
          }

          const visibleChildren = this.getVisibleMenu(item.children);
          if (visibleChildren.length > 0) {
            return { ...item, children: visibleChildren };
          }
          return null;
        } else if (
          this.isMenuVisibleForOrganizationLevel(item) &&
          this.hasMenuPermission(item)
        ) {
          return item;
        }
        return null;
      })
      .filter((item): item is menu => item !== null);
  }

  private hasMenuPermission(item: menu): boolean {
    if (item.route === '/manage-settings/options') {
      return this.optionsMenuPermissions.some(permission =>
        this.rbac.hasPermission(permission)
      );
    }

    return !item.permission || this.rbac.hasPermission(item.permission);
  }

  private isMenuVisibleForOrganizationLevel(item: menu): boolean {
    if (!item.route.startsWith('/manage-settings/')) {
      return true;
    }

    // If selected org is parent (parentOrganizationId === null), only show Custom Time and Options
    if (!this.isParentOrganization) {
      return (
        item.route === '/manage-settings/custom-time' ||
        item.route === '/manage-settings/options'
      );
    }
    // For parent org, show all
    return true;
  }
  checkSupportPermission() {
    this.isSupport = this.rbac.hasPermission('view_support');
  }

  checkSupportLoginPermission() {
    this.isSupportLogin = this.rbac.hasPermission('view_support_login');
  }

  ngOnInit() {
    this.commonService.orgList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.orgList = orgs;
      });

    this.notificationService.selectedOrgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgId => {
        this.selectedOrgId = orgId;

        this.notificationService.clearNotifications();
      });
    this.checkScreenSize();

    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkScreenSize();
      });

    this.layoutService.sidebarState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.isCollapsed !== state) {
          this.isCollapsed = state;
          if (!state && this.isMobile) {
            document.body.style.overflow = 'hidden';
          } else {
            document.body.style.overflow = '';
          }
          this.cd.detectChanges();
        }
      });

    const orgId =
      this.selectedOrgId && this.selectedOrgId > 0 ? this.selectedOrgId : null;
    this.notificationService
      .getNotificationsByUserId(NotificationFilterType.All, orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: error => {
          console.error('Error loading notifications:', error);
        },
      });

    if (orgId === null && !this.userData?.isSuperAdmin) {
      this.broadcastService
        .getBroadcastNotificationsByOrgId(orgId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response?.result) {
              this.notificationService.combineWithBroadcastNotifications(
                response.result
              );
            }
          },
          error: error => {
            console.error('Error loading broadcast notifications:', error);
          },
        });
    }
  }

  private checkScreenSize() {
    const previousIsMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;

    if (previousIsMobile !== this.isMobile) {
      if (this.isMobile) {
        this.isCollapsed = true;
        this.layoutService.setSidebarState(true);
      } else {
        this.isCollapsed = false;
        this.layoutService.setSidebarState(false);
        document.body.style.overflow = '';
      }
      this.cd.detectChanges();
    }

    if (!this.isCollapsed && this.isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  closeSidebar(): void {
    this.layoutService.setSidebarState(true);
    document.body.style.overflow = '';
    this.isCollapsed = true;
    this.cd.detectChanges();
  }

  hitBackgroundUrl(): void {
    this.loading = true;
    const url = this.environmentService.getLogoutUrl();
    // NOSONAR: Safe to bypass sanitization - URL is from environment configuration
    // (not user input) and restricted to trusted internal domains (insightstobehavior.net)
    if (url) {
      this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url); // NOSONAR
    }
    localStorage.setItem('isLMSLoggedIn', 'false');
  }

  isSubmenuOpen(item: menu): boolean {
    if (!item.children?.length) {
      return false;
    }

    return item.children.some(child => this.router.url.startsWith(child.route));
  }

  get environmentInfo() {
    return this.environmentService.environmentInfo;
  }

  isVisible = false;
  showLogoutModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
  }
  handleCancel(): void {
    this.isVisible = false;
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeSubscription.unsubscribe();
  }

  formatNotificationCount(count: number): string {
    return count > 99 ? '99+' : count.toString();
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  resetNotificationAnimation(): void {
    this.isNewNotification = false;
  }
}
